import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

import ModalWindow from './ModalWindow';
import LegendCP from './LegendCP';
import SubMenu from './SubMenu';
import LoadingSpinner from './LoadingSpinner';
import CurrencyFormat from './CurrencyFormat';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const InventoryListCp = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [notesIdOpen, setNotesIdOpen] = useState(null);

  const [locationList, setLocationList] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');

  const [fulltotal, setFullTotal] = useState(0);

  // 🔹 NEW: default threshold from inventory_settings
  const [defaultThreshold, setDefaultThreshold] = useState(10); // fallback 10

  const apiUrl = process.env.REACT_APP_API_URL; 
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();   // 🔹 NEW
    fetchInventory();
    fetchLocations();
  }, []);

  // --- NEW: Fetch default threshold from inventory_crud ---
  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${apiUrl}/inventory_crud.php`, {
        params: { getInventorySettings: true },
        headers: { 'Content-Type': 'application/json' },
      });

      if (Array.isArray(res.data) && res.data[0] === 'success') {
        const s = res.data[1] || {};
        if (s.default_threshold !== undefined && s.default_threshold !== null) {
          setDefaultThreshold(Number(s.default_threshold) || 10);
        }
      } else if (res.data?.success && res.data.data) {
        const s = res.data.data;
        if (s.default_threshold !== undefined && s.default_threshold !== null) {
          setDefaultThreshold(Number(s.default_threshold) || 10);
        }
      }
    } catch (err) {
      console.error('Error fetching inventory settings', err);
      // keep fallback 10
    }
  };

  // --- 1) Fetch Inventory
  const fetchInventory = async (locID = '') => {
    try {
      setLoading(true);
      let endpoint = `${apiUrl}/inventory_crud.php?inventory=true`;
      if (locID) {
        endpoint += `&locID=${locID}`;
      }
      const response = await axios.get(endpoint, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (Array.isArray(response.data) && response.data[0] === 'success') {
        setFullTotal(response.data[2] || 0);
        setInventoryData(response.data[1]);
        setFilteredInventory(response.data[1]);
      } else {
        setFilteredInventory([]);
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Error fetching data', { position: 'top-center' });
      }
    } finally {
      setLoading(false);
    }
  };

  // --- 2) Fetch all possible locations
  const fetchLocations = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/inventory_crud.php?inventoryLocation=true`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (Array.isArray(res.data) && res.data[0] === 'success') {
        setLocationList(res.data[1]);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (e) => {
    const locID = e.target.value;
    setSelectedLocation(locID);
    fetchInventory(locID);
  };

  // Local search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (!val) {
      setFilteredInventory(inventoryData);
    } else {
      const filtered = inventoryData.filter((item) =>
        (item.device && item.device.toLowerCase().includes(val.toLowerCase())) ||
        (item.notes && item.notes.toLowerCase().includes(val.toLowerCase())) ||
        (item.category && item.category.toLowerCase().includes(val.toLowerCase())) ||
        (item.device_id && item.device_id.toString().includes(val))
      );
      setFilteredInventory(filtered);
    }
  };

  const handleSearchClick = async () => {
    if (!searchTerm) {
      setFilteredInventory(inventoryData);
      return;
    }
    try {
      setLoading(true);
      const bodyData = { status: 'searchInventory', data: { search: searchTerm } };
      const response = await axios.post(`${apiUrl}/inventory_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data && Array.isArray(response.data)) {
        setFilteredInventory(response.data[1]);
      } else {
        setFilteredInventory([]);
        toast.error(`Not found: ${searchTerm}`, { position: 'top-center' });
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setFilteredInventory(inventoryData);
  };

  const getLinksForItem = (record) => {
    return [
      { name: 'Edit Item', click: () => openEditModal(record) }
    ];
  };

  const openEditModal = (record) => {
    setEditData({
      inventory_id: record.inventory_id,
      reorder_level: record.reorder_level || 0,
      notes: record.notes || '',
      location_id: record.location_id
    });
    setShowEditModal(true);
  };

  const handleUpdateInventory = async (payload) => {
    if (!payload.location_id) {
      toast.error('Please select a location', { position: 'top-center' });
      return;
    }
    try {
      const response = await axios.post(`${apiUrl}/inventory_crud.php`, {
        status: 'updateInventoryItem',
        data: payload
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.data.success) {
        toast.success('Inventory updated!', { position: 'top-center' });
        setShowEditModal(false);
        fetchInventory(selectedLocation);
      } else {
        toast.error('Error updating item', { position: 'top-center' });
      }
    } catch (err) {
      console.error(err);
      toast.error('Error updating item', { position: 'top-center' });
    }
  };


    // Export to CSV
  const exportToCSV = () => {
    if (!filteredInventory.length) {
      toast.info('No inventory data to export.', { position: 'top-center' });
      return;
    }

    const headers = [
      'ID',
      'Device',
      'Manufacture',
      'Description',
      'Location',
      'Quantity',
      'Price',
      'Total',
      'Reorder Level',
      'Notes',
    ];

    const rows = filteredInventory.map((item) => [
      item.inventory_id,
      item.device_name,
      item.manufacture,
      item.full_description,
      item.location,
      item.current_inventory,
      item.device_price,
      item.total,
      item.reorder_level === 0 ? 10 : item.reorder_level,
      item.notes,
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `inventory-export-${date}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="mx-auto max-w-[1250px] px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-green-800 font-bold text-md p-3">
          {fulltotal > 0 && <span>Total: <CurrencyFormat value={fulltotal} /></span>}
        </p>
        {/* 🔹 Legend now uses defaultThreshold */}
        <LegendCP color="fill-yellow-300" label={`Inventory @${defaultThreshold} (default)`} /> 
        <LegendCP color="fill-red-500" label="Below Reorder Lvl" /> 
        <br /><br />

        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
          >
            Export CSV
          </button>

          {/* <button
            onClick={() => setShowAddModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold"
          >
            + Add Item
          </button> */}
        </div>            
        <br /> 

        {/* SELECT location + Search bar */}
        <div className="flex items-center space-x-3 mb-3">
          <select
            className="border border-gray-300 rounded-md px-6 py-2"
            value={selectedLocation}
            onChange={handleLocationChange}
          >
            <option value="">All Locations</option>
            {locationList.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.location} {loc.sub_location}
              </option>
            ))}
          </select>

          <div className="relative flex-grow">
            <input
              type="text"
              name="search"
              value={searchTerm}
              required
              onChange={handleSearchChange}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchClick();
                }
              }}               
              className="block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
              placeholder="Enter a search term"
            />
            <button
              onClick={handleSearchClick}
              disabled={!searchTerm}              
              className={`absolute right-24 top-0 bottom-0 text-white px-3 py-2 ${
                !searchTerm ? 'bg-gray-500' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              Search
            </button>
            <button
              onClick={handleReset}
              className="absolute right-0 top-0 bottom-0 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-r-md"
            >
              <span className="flex items-center gap-1">
                <ArrowPathIcon className="h-5" />
                Reset
              </span>
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : (
          <div>
            <table className="w-full divide-y divide-gray-300 bg-gray-200 border rounded-lg">
              <thead>
                <tr className="divide-x divide-gray-400">
                  <th className="py-3 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">ID</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">
                    Device | Manufacture
                  </th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Location</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Qty</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Price</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Ttl</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Lvl</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-gray-500">
                      No results found.
                    </td>
                  </tr>
                ) : ( 
                  filteredInventory.map((item, index) => {
                    const currentInv = Number(item.current_inventory || 0);
                    const reorderLvl = Number(item.reorder_level || 0);

                    const rowClass = (() => {
                      // Below custom reorder level
                      if (reorderLvl > 0 && currentInv <= reorderLvl) {
                        return 'divide-x bg-red-200';
                      }
                      // Using default threshold when reorder_level is 0/null
                      if ((reorderLvl === 0 || item.reorder_level == null) &&
                          currentInv <= defaultThreshold) {
                        return 'divide-x bg-yellow-100';
                      }
                      // Stripe
                      return index % 2 === 0 ? 'divide-x bg-white' : 'divide-x bg-gray-100';
                    })();

                    return (
                      <tr key={item.inventory_id} className={rowClass}>
                        <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-700">
                          <SubMenu
                            label={item.inventory_id}
                            idItem={item.inventory_id}
                            links={getLinksForItem(item)}
                          />
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <p className="font-semibold">{item.device_name}</p>
                          <p className="text-gray-400">{item.manufacture}</p>
                        </td>
                        <td className="px-1 py-2 text-sm text-gray-500">
                          {item.full_description}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.location}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {currentInv}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <CurrencyFormat value={item.device_price} />
                        </td>
                        <td className="px-3 py-4 text-sm text-green-700">
                          <CurrencyFormat value={item.total} />
                        </td>
                        {/* 🔹 Show defaultThreshold when reorder_level is 0 */}
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {reorderLvl === 0 ? defaultThreshold : reorderLvl}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500 max-w-xs">
                          {item.notes?.length > 50 ? (
                            <>
                              {item.notes.substring(0, 50)}...
                              <button
                                className="ml-2 text-blue-500 underline"
                                onClick={() => setNotesIdOpen(item.inventory_id)}
                              >
                                View More
                              </button>
                              {notesIdOpen === item.inventory_id && (
                                <ModalWindow
                                  job={item.notes}
                                  closeNotes={() => setNotesIdOpen(null)}
                                  activeButton
                                />
                              )}
                            </>
                          ) : (
                            item.notes
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditModal && editData && (
        <ModalWindow
          closeNotes={() => setShowEditModal(false)}
          activeButton
          job={
            <EditItemForm
              editData={editData}
              locationList={locationList}
              onClose={() => setShowEditModal(false)}
              onSave={handleUpdateInventory}
            />
          }
        />
      )}
    </>
  );
}

function EditItemForm({ editData, locationList, onClose, onSave }) {
  const [reorderLevel, setReorderLevel] = useState(editData.reorder_level || 0);
  const [notes, setNotes] = useState(editData.notes || '');
  const [locId, setLocId] = useState(editData.location_id || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      inventory_id: editData.inventory_id,
      reorder_level: reorderLevel,
      notes,
      location_id: locId
    };
    onSave(payload);
  };

  
  return (
    <div className="p-4">
      <h2 className="text-lg font-bold mb-2">Edit Inventory #{editData.inventory_id}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        
        <div>
          <label className="block text-sm font-semibold mb-1">Reorder Level</label>
          <input
            type="number"
            className="border rounded p-2 w-full"
            value={reorderLevel}
            onChange={(e) => setReorderLevel(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Notes</label>
          <textarea
            rows="3"
            className="border rounded p-2 w-full"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Location</label>
          <select
            className="border rounded p-2 w-full"
            value={locId}
            onChange={(e) => setLocId(e.target.value)}
            disabled={true}
          >
            <option value="">Select location</option>
            {locationList.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.location} {loc.sub_location}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end pt-4 space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-1 rounded"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1 rounded"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default InventoryListCp;
